import React, { useState, useEffect } from 'react';
import { Wifi, Globe, Shield, Database, Search, AlertTriangle, Loader2 } from 'lucide-react';
import Navbar from './VScanner/NavBar';

const VScanner = () => {
  const [inputValue, setInputValue] = useState('');
  const [inputType, setInputType] = useState('domain');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('whois');
  const [scanProgress, setScanProgress] = useState(0);

  // Domain validation regex
  const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  // IP validation regex
  const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

  const isInputValid = () => {
    if (inputType === 'domain') {
      return domainRegex.test(inputValue);
    } else {
      return ipRegex.test(inputValue);
    }
  };

  const performScan = async () => {
    if (!isInputValid()) {
        setError(inputType === 'domain' ? 
            'Invalid domain format. Use format like "google.com"' : 
            'Invalid IP address format. Use format like "192.168.1.1"');
        return;
    }
    
    setError(null);
    setIsScanning(true);
    setScanProgress(0);
    setScanResults(null);

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 1200000);

    try {
        const progressInterval = setInterval(() => {
            setScanProgress(prev => Math.min(prev + Math.random() * 10, 90));
        }, 500);

        const response = await fetch('/v1/VScan/vuln-scan', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                target: inputValue
            }),
            signal: abortController.signal,
        });

        clearTimeout(timeoutId);
        clearInterval(progressInterval);
        await new Promise(resolve => setTimeout(resolve, 300));
        setScanProgress(100);

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch (jsonError) {
                const text = await response.text();
                throw new Error(`Scan failed with status ${response.status}: ${text}`);
            }
            throw new Error(errorData.error || `Scan failed with status ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            throw new Error('Invalid response format (expected JSON)');
        }

        const data = await response.json();
        setScanResults(data);

    } catch (err) {
        if (err.name === 'AbortError') {
            setError('Scan timed out. Please try again.');
        } else if (err instanceof TypeError && err.message === 'Failed to fetch') {
            setError('Network error. Please check your connection and try again.');
        } else {
            setError(err.message || 'An unexpected error occurred during the scan.');
        }
    } finally {
        setIsScanning(false);
    }
  };

  // Format date arrays or handle null values
  const formatDate = (dateArray) => {
    if (!dateArray || dateArray.length === 0) return 'N/A';
    if (typeof dateArray === 'string') return dateArray;
    return dateArray[0];
  };

  // For visualization - count open ports
  const getPortStatusCounts = () => {
    if (!scanResults?.ports?.open_ports) return { open: 0, filtered: 0, closed: 0 };
    
    const counts = { open: 0, filtered: 0, closed: 0 };
    Object.values(scanResults.ports.open_ports).forEach(status => {
      if (status === 'open') counts.open++;
      else if (status === 'filtered') counts.filtered++;
      else counts.closed++;
    });
    return counts;
  };

  // For visualization - security header score
  const getSecurityHeaderScore = () => {
    if (!scanResults?.security_headers) return 0;
    
    const headers = scanResults.security_headers;
    let score = 0;
    let total = 0;
    
    for (const [header, value] of Object.entries(headers)) {
      total++;
      if (value !== 'Missing' && value !== '0') {
        score++;
      }
    }
    
    return total > 0 ? Math.round((score / total) * 100) : 0;
  };

  // Render result section with error handling
  const renderResultSection = (title, data, renderContent) => {
    if (!data) return null;
    
    if (data.error) {
      return (
        <div className="bg-gray-850 rounded-lg p-4 shadow-inner mb-4">
          <h4 className="text-md font-medium text-indigo-300 mb-2">{title}</h4>
          <div className="text-red-400 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span>{data.error}</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="bg-gray-850 rounded-lg p-4 shadow-inner mb-4">
        <h4 className="text-md font-medium text-indigo-300 mb-2">{title}</h4>
        {renderContent()}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 flex flex-col">
      {/* Header */}
      
      <header className=" py-8">
      <Navbar />
      </header>

      {/* Main content */}
      <main className="flex-grow container mx-auto px-4 py-6">
        {/* Input section */}
        <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-6 border border-gray-700">
          <h2 className="text-xl font-semibold mb-4 text-indigo-300">Target Configuration</h2>
          
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2 text-gray-300">Target Type</label>
              <div className="flex rounded-md overflow-hidden">
                <button 
                  className={`flex-1 py-2 px-4 ${inputType === 'domain' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                  onClick={() => setInputType('domain')}
                >
                  <Globe className="inline-block mr-2 h-4 w-4" />
                  Domain
                </button>
                <button 
                  className={`flex-1 py-2 px-4 ${inputType === 'ip' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'}`}
                  onClick={() => setInputType('ip')}
                >
                  <Wifi className="inline-block mr-2 h-4 w-4" />
                  IP Address
                </button>
              </div>
            </div>
            
            <div className="flex-grow">
              <label className="block text-sm font-medium mb-2 text-gray-300">
                {inputType === 'domain' ? 'Domain Name' : 'IP Address'}
              </label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={inputType === 'domain' ? 'example.com' : '192.168.1.1'}
                  className={`block w-full py-2 px-3 bg-gray-700 border ${isInputValid() || !inputValue ? 'border-gray-600' : 'border-red-500'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white`}
                />
                {inputType === 'domain' && inputValue && !isInputValid() && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  </div>
                )}
              </div>
              {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
            </div>
            
            <div className="flex items-end">
              <button
                onClick={performScan}
                disabled={isScanning || !inputValue}
                className={`flex items-center justify-center px-6 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  isScanning || !inputValue
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                }`}
              >
                {isScanning ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Start Scan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Scanner animation */}
        {isScanning && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="relative w-32 h-32">
              <div className="absolute top-0 left-0 w-full h-full border-4 border-indigo-500 rounded-full opacity-20"></div>
              <div className="absolute top-0 left-0 w-full h-full border-4 border-indigo-500 rounded-full opacity-40 animate-ping" style={{ animationDuration: '2s' }}></div>
              <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                <Search className="h-12 w-12 text-indigo-400" />
              </div>
            </div>
            <p className="mt-6 text-lg text-indigo-300">Scanning {inputValue}...</p>
            <p className="text-sm text-gray-400">This may take a few moments</p>
            <div className="w-full max-w-md mt-4 bg-gray-800 rounded-full h-2.5">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${scanProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Results section */}
        {!isScanning && scanResults && (
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
            <div className="border-b border-gray-700 p-4 flex items-center">
              <Shield className="h-5 w-5 text-indigo-400 mr-2" />
              <h2 className="text-xl font-semibold text-indigo-300">Scan Results for {inputValue}</h2>
            </div>
            
            {/* Dashboard summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
              {/* Target Info Card */}
              <div className="bg-gray-850 rounded-lg p-4 shadow-inner">
                <h4 className="text-md font-medium text-indigo-300 mb-2">Target Information</h4>
                <div className="text-sm">
                  <p className="flex justify-between py-1 border-b border-gray-700">
                    <span className="text-gray-400">Type:</span>
                    <span className="font-medium">{inputType === 'domain' ? 'Domain' : 'IP Address'}</span>
                  </p>
                  <p className="flex justify-between py-1 border-b border-gray-700">
                    <span className="text-gray-400">Target:</span>
                    <span className="font-medium">{inputValue}</span>
                  </p>
                  {scanResults.dns?.ip_address && (
                    <p className="flex justify-between py-1 border-b border-gray-700">
                      <span className="text-gray-400">IP:</span>
                      <span className="font-medium">{scanResults.dns.ip_address}</span>
                    </p>
                  )}
                  {scanResults.whois?.org && (
                    <p className="flex justify-between py-1 border-b border-gray-700">
                      <span className="text-gray-400">Organization:</span>
                      <span className="font-medium">{scanResults.whois.org}</span>
                    </p>
                  )}
                </div>
              </div>
              
              {/* Port Status Card */}
              {renderResultSection('Port Status', scanResults.ports, () => (
                <div className="flex items-center justify-around h-32">
                  <div className="relative w-32 h-32">
                    <svg viewBox="0 0 100 100">
                      {(() => {
                        const stats = getPortStatusCounts();
                        const total = stats.open + stats.filtered + stats.closed;
                        let startAngle = 0;
                        const result = [];
                        
                        if (stats.open > 0) {
                          const angle = (stats.open / total) * 360;
                          result.push(
                            <path 
                              key="open"
                              d={`M 50,50 L ${50 + 40 * Math.cos(startAngle * Math.PI / 180)},${50 + 40 * Math.sin(startAngle * Math.PI / 180)} A 40,40 0 ${angle > 180 ? 1 : 0},1 ${50 + 40 * Math.cos((startAngle + angle) * Math.PI / 180)},${50 + 40 * Math.sin((startAngle + angle) * Math.PI / 180)} Z`}
                              fill="#22c55e"
                            />
                          );
                          startAngle += angle;
                        }
                        
                        if (stats.filtered > 0) {
                          const angle = (stats.filtered / total) * 360;
                          result.push(
                            <path 
                              key="filtered"
                              d={`M 50,50 L ${50 + 40 * Math.cos(startAngle * Math.PI / 180)},${50 + 40 * Math.sin(startAngle * Math.PI / 180)} A 40,40 0 ${angle > 180 ? 1 : 0},1 ${50 + 40 * Math.cos((startAngle + angle) * Math.PI / 180)},${50 + 40 * Math.sin((startAngle + angle) * Math.PI / 180)} Z`}
                              fill="#f59e0b"
                            />
                          );
                          startAngle += angle;
                        }
                        
                        if (stats.closed > 0) {
                          const angle = (stats.closed / total) * 360;
                          result.push(
                            <path 
                              key="closed"
                              d={`M 50,50 L ${50 + 40 * Math.cos(startAngle * Math.PI / 180)},${50 + 40 * Math.sin(startAngle * Math.PI / 180)} A 40,40 0 ${angle > 180 ? 1 : 0},1 ${50 + 40 * Math.cos((startAngle + angle) * Math.PI / 180)},${50 + 40 * Math.sin((startAngle + angle) * Math.PI / 180)} Z`}
                              fill="#ef4444"
                            />
                          );
                        }
                        
                        return result;
                      })()}
                      <circle cx="50" cy="50" r="25" fill="#1f2937" />
                    </svg>
                  </div>
                  
                  <div className="text-sm">
                    <div className="flex items-center mb-2">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      <span className="text-gray-300">Open: {getPortStatusCounts().open}</span>
                    </div>
                    <div className="flex items-center mb-2">
                      <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
                      <span className="text-gray-300">Filtered: {getPortStatusCounts().filtered}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                      <span className="text-gray-300">Closed: {getPortStatusCounts().closed}</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Security Score Card */}
              <div className="bg-gray-850 rounded-lg p-4 shadow-inner">
                <h4 className="text-md font-medium text-indigo-300 mb-2">Security Score</h4>
                <div className="flex flex-col items-center justify-center h-32">
                  <div className="relative">
                    <svg width="120" height="120" viewBox="0 0 120 120">
                      <circle
                        cx="60"
                        cy="60"
                        r="54"
                        fill="none"
                        stroke="#374151"
                        strokeWidth="12"
                      />
                      <circle
                        cx="60"
                        cy="60"
                        r="54"
                        fill="none"
                        stroke={
                          getSecurityHeaderScore() > 70 
                            ? "#22c55e" 
                            : getSecurityHeaderScore() > 40 
                              ? "#f59e0b" 
                              : "#ef4444"
                        }
                        strokeWidth="12"
                        strokeDasharray={`${3.39 * getSecurityHeaderScore()} 339.292`}
                        strokeLinecap="round"
                        transform="rotate(-90 60 60)"
                      />
                      <text
                        x="60"
                        y="65"
                        textAnchor="middle"
                        fontSize="24"
                        fontWeight="bold"
                        fill="white"
                      >
                        {getSecurityHeaderScore()}%
                      </text>
                    </svg>
                  </div>
                  
                  <div className="text-sm mt-2 text-center">
                    <p className="text-gray-400">
                      {getSecurityHeaderScore() > 70 
                        ? "Good security posture" 
                        : getSecurityHeaderScore() > 40 
                          ? "Moderate security risks" 
                          : "High security risks"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Detailed results tabs */}
            <div className="p-4">
              <div className="border-b border-gray-700">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('whois')}
                    className={`py-2 px-4 text-sm font-medium ${
                      activeTab === 'whois'
                        ? 'text-indigo-400 border-b-2 border-indigo-400'
                        : 'text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    WHOIS & DNS
                  </button>
                  <button
                    onClick={() => setActiveTab('ports')}
                    className={`py-2 px-4 text-sm font-medium ${
                      activeTab === 'ports'
                        ? 'text-indigo-400 border-b-2 border-indigo-400'
                        : 'text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    Ports & Services
                  </button>
                  <button
                    onClick={() => setActiveTab('security')}
                    className={`py-2 px-4 text-sm font-medium ${
                      activeTab === 'security'
                        ? 'text-indigo-400 border-b-2 border-indigo-400'
                        : 'text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    Security Analysis
                  </button>
                </nav>
              </div>
              
              {/* Tab content */}
              <div className="py-4">
                {/* WHOIS & DNS Tab */}
                {activeTab === 'whois' && (
                  <>
                    {renderResultSection('Domain Information', scanResults.whois, () => (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between py-1 border-b border-gray-700">
                              <span className="text-gray-400">Domain Name:</span>
                              <span className="font-medium">{scanResults.whois.domain_name || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700">
                              <span className="text-gray-400">Registrar:</span>
                              <span className="font-medium">{scanResults.whois.registrar || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700">
                              <span className="text-gray-400">Organization:</span>
                              <span className="font-medium">{scanResults.whois.org || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700">
                              <span className="text-gray-400">Creation Date:</span>
                              <span className="font-medium">{formatDate(scanResults.whois.creation_date)}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700">
                              <span className="text-gray-400">Expiration Date:</span>
                              <span className="font-medium">{formatDate(scanResults.whois.expiration_date)}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700">
                              <span className="text-gray-400">DNSSEC:</span>
                              <span className="font-medium">{scanResults.whois.dnssec || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="text-sm font-medium text-gray-300 mb-2">Name Servers</h5>
                          {scanResults.whois.name_servers?.length > 0 ? (
                            <ul className="list-disc list-inside space-y-1 text-gray-400">
                              {scanResults.whois.name_servers.map((ns, i) => (
                                <li key={i}>{ns}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-500">No name servers found</p>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {renderResultSection('DNS Information', scanResults.dns, () => (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="flex justify-between py-1 border-b border-gray-700">
                            <span className="text-gray-400">IP Address:</span>
                            <span className="font-medium">{scanResults.dns.ip_address || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                
                {/* Ports & Services Tab */}
                {activeTab === 'ports' && (
                  renderResultSection('Open Ports & Services', scanResults.ports, () => (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                          <thead>
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Port
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Service
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Security Risk
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {scanResults.ports?.open_ports && Object.entries(scanResults.ports.open_ports).map(([port, status]) => (
                              <tr key={port} className={status === 'open' ? 'bg-green-900 bg-opacity-10' : ''}>
                                <td className="px-4 py-2 text-sm font-medium text-white">
                                  {port}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-300">
                                  {scanResults.ports.service_versions?.[port] || 'Unknown'}
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    status === 'open' 
                                      ? 'bg-green-900 text-green-300' 
                                      : status === 'filtered'
                                        ? 'bg-yellow-900 text-yellow-300'
                                        : 'bg-red-900 text-red-300'
                                  }`}>
                                    {status}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-300">
                                  {(() => {
                                    const riskMap = {
                                      21: 'High - FTP often lacks encryption',
                                      22: 'Low - SSH is typically secure',
                                      23: 'Critical - Telnet has no encryption',
                                      25: 'Medium - SMTP can be abused for spam',
                                      80: 'Low - HTTP is standard for web traffic',
                                      443: 'Low - HTTPS provides encryption',
                                      3389: 'Medium - Remote desktop access point'
                                    };
                                    
                                    return status === 'open' 
                                      ? (riskMap[port] || 'Unknown')
                                      : 'N/A';
                                  })()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-6 bg-gray-800 p-4 rounded-md border border-gray-700">
                        <h5 className="text-sm font-medium text-indigo-300 mb-2">Summary Analysis</h5>
                        <p className="text-sm text-gray-300">
                          {(() => {
                            const openPorts = Object.values(scanResults.ports?.open_ports || {}).filter(status => status === 'open').length;
                            if (openPorts === 0) {
                              return 'No open ports were detected. This reduces the attack surface but may indicate filtering is in place.';
                            } else if (openPorts <= 2) {
                              return `${openPorts} open ports detected. This suggests a relatively small attack surface.`;
                            } else {
                              return `${openPorts} open ports detected. Consider closing unnecessary services to reduce attack surface.`;
                            }
                          })()}
                        </p>
                      </div>
                    </>
                  ))
                )}
                
                {/* Security Analysis Tab */}
                {activeTab === 'security' && (
                  <>
                    {renderResultSection('Security Headers', scanResults.security_headers, () => (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          {Object.entries(scanResults.security_headers || {}).map(([header, value]) => (
                            <div key={header} className="flex items-start">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-2 mt-1 ${
                                value === 'Missing' || value === '0'
                                  ? 'bg-red-500'
                                  : 'bg-green-500'
                              }`}>
                                {value === 'Missing' || value === '0' ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-300">{header}</div>
                                <div className="text-xs text-gray-400">
                                  {value === 'Missing' ? 'Not implemented' : value}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {renderResultSection('SSL Analysis', scanResults.ssl, () => (
                      <div className="flex items-start mb-4">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-2 ${
                          scanResults.ssl?.valid
                            ? 'bg-green-500'
                            : 'bg-red-500'
                        }`}>
                          {scanResults.ssl?.valid ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-300">SSL Certificate</div>
                          <div className="text-xs text-gray-400">
                            {scanResults.ssl?.valid
                              ? 'Certificate is valid and trusted'
                              : 'Certificate has issues or is not implemented'}
                          </div>
                          {scanResults.ssl?.certificate_info && (
                            <div className="mt-2 p-2 bg-gray-700 rounded text-xs font-mono overflow-x-auto">
                              {scanResults.ssl.certificate_info}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {renderResultSection('Open Directories', scanResults.open_directories, () => (
                    <div>
                        {typeof scanResults.open_directories?.open_directories === 'string' ? (
                        // Handle string response case
                        scanResults.open_directories.open_directories === "No open directories found" ? (
                            <p className="text-gray-400">No open directories detected</p>
                        ) : (
                            <p className="text-gray-400">{scanResults.open_directories.open_directories}</p>
                        )
                        ) : Array.isArray(scanResults.open_directories?.open_directories) ? (
                        // Handle array response case
                        scanResults.open_directories.open_directories.length === 0 ? (
                            <p className="text-gray-400">No open directories detected</p>
                        ) : (
                            <ul className="list-disc list-inside space-y-1 text-gray-300">
                            {scanResults.open_directories.open_directories.map((dirObj, i) => (
                                <li key={i}>
                                <a 
                                    href={typeof dirObj === 'string' ? dirObj : dirObj.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="text-indigo-400 hover:underline"
                                >
                                    {typeof dirObj === 'string' ? dirObj : `${dirObj.directory} (Status: ${dirObj.status_code})`}
                                </a>
                                </li>
                            ))}
                            </ul>
                        )
                        ) : (
                        // Fallback case
                        <p className="text-gray-400">No directory information available</p>
                        )}
                    </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
    <footer className="bg-gray-800 border-t border-gray-700 py-8 mt-auto">
    <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* About Section */}
        <div>
            <h3 className="text-lg font-semibold text-indigo-400 mb-4">ThreatEye Pro</h3>
            <p className="text-gray-400 text-sm mb-4">
            Advanced vulnerability scanning and threat assessment platform for modern security teams.
            </p>
            <div className="flex space-x-4">
            <a href="#" className="text-gray-400 hover:text-indigo-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
            </a>
            <a href="#" className="text-gray-400 hover:text-indigo-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                </svg>
            </a>
            <a href="#" className="text-gray-400 hover:text-indigo-400 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
            </a>
            </div>
        </div>

        {/* Features Section */}
        <div>
            <h3 className="text-lg font-semibold text-indigo-400 mb-4">Key Features</h3>
            <ul className="space-y-2 text-sm text-gray-400">
            <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Comprehensive vulnerability scanning
            </li>
            <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Real-time threat detection
            </li>
            <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Detailed security reporting
            </li>
            <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Continuous monitoring
            </li>
            <li className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                API integration capabilities
            </li>
            </ul>
        </div>

        {/* Contact/Legal Section */}
        <div>
            <h3 className="text-lg font-semibold text-indigo-400 mb-4">Contact & Legal</h3>
            <ul className="space-y-2 text-sm text-gray-400">
            <li>
                <a href="#" className="hover:text-indigo-400 transition-colors">Privacy Policy</a>
            </li>
            <li>
                <a href="#" className="hover:text-indigo-400 transition-colors">Terms of Service</a>
            </li>
            <li>
                <a href="#" className="hover:text-indigo-400 transition-colors">Documentation</a>
            </li>
            <li>
                <a href="#" className="hover:text-indigo-400 transition-colors">Support</a>
            </li>
            <li className="pt-2">
                Developed by <span className="text-indigo-400">F/N Nuru The_Geek</span>
            </li>
            </ul>
        </div>
        </div>

        {/* Copyright Section */}
        <div className="border-t border-gray-700 mt-8 pt-6 text-center">
        <p className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} ThreatEye Pro. All rights reserved.
        </p>
        <p className="text-xs text-gray-500 mt-1">
            Vulnerability scanner v1.0.0 | Last updated: {new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
            })}
        </p>
        </div>
    </div>
    </footer>
    </div>
  );
};

export default VScanner;
